import { breakpoints } from '@naf/theme';
import { CloudinaryImageType } from '@appTypes/CloudinaryImageType';
import { HeroImageVariant } from './HeroImages.types';

export const getPublicId = (image?: CloudinaryImageType | null) => image?.public_id || image?.publicId;

const PORTRAIT_CROP = '9:16';
const BELOW_SMALL = `(max-width: ${parseInt(breakpoints.s, 10) - 1}px)`;
const SMALL_PORTRAIT = `${BELOW_SMALL} and (orientation: portrait)`;
const UP_TO_SMALL = `(max-width: ${breakpoints.s})`;

// ArticleHeader covers a box of these heights, so on tall screens an image is as wide as the box height
// times its aspect ratio. The main image is assumed 16:9 like ArticleHeader does, a mobile image at most square.
const getCoverWidth = (boxHeightVh: number, imageAspectRatio: number) =>
  `max(100vw, ${Math.ceil(boxHeightVh * imageAspectRatio)}vh)`;

export const getVariants = (
  image: CloudinaryImageType,
  mobileImage: CloudinaryImageType | undefined,
  cropOnSmallScreens: boolean,
  halfScreen: boolean,
): HeroImageVariant[] => {
  const smallBoxHeightVh = halfScreen ? 35 : 85;
  const canCrop = cropOnSmallScreens && !halfScreen;
  const getCroppedVariant = (cropped: CloudinaryImageType): HeroImageVariant => ({
    image: cropped,
    media: SMALL_PORTRAIT,
    aspectRatio: PORTRAIT_CROP,
    sizes: [getCoverWidth(smallBoxHeightVh, 9 / 16)],
  });

  const mobile = mobileImage && getPublicId(mobileImage) ? mobileImage : undefined;
  const variants: HeroImageVariant[] = [];
  if (mobile) {
    if (canCrop && mobile.focus) variants.push(getCroppedVariant(mobile));
    variants.push({ image: mobile, media: UP_TO_SMALL, sizes: [getCoverWidth(smallBoxHeightVh, 1)] });
  } else if (canCrop && image.focus) {
    variants.push(getCroppedVariant(image));
  }

  const largeScreenSizes = halfScreen ? getCoverWidth(47, 16 / 9) : '100vw';
  variants.push({
    image,
    sizes: mobile
      ? [largeScreenSizes]
      : [`${BELOW_SMALL} ${getCoverWidth(smallBoxHeightVh, 16 / 9)}`, largeScreenSizes],
  });
  return variants;
};

export const getHiddenMedia = (earlierVariants: HeroImageVariant[]) =>
  earlierVariants.flatMap(({ media }) => (media ? [media] : []));

export const getSizes = (variant: HeroImageVariant, earlierVariants: HeroImageVariant[]) =>
  [
    ...getHiddenMedia(earlierVariants).map((media) => `${media} 1px`),
    ...(variant.media ? [`${variant.media} ${variant.sizes[0]}`, '1px'] : variant.sizes),
  ].join(', ');
